import * as React from "react";

import type {
	QueryCallbacks,
	QueryConfig,
	QueryErrorState,
	QueryResults,
	QuerySchema,
} from "../types";

export interface UseQueryStateProps extends QueryCallbacks {
	/** Available query configurations */
	queries: QueryConfig[];
	/** Last used query ID for session persistence */
	lastUsedQuery?: string | null;
	/** Simulate loading error for testing */
	simulateError?: boolean;
	/** Simulate query results for testing */
	simulateResults?: "success" | "no_results" | "too_many_results" | "error";
}

// Helper function to calculate default filter values for a schema
function getDefaultFilterValues(schema: QuerySchema): Record<string, any> {
	const defaults: Record<string, any> = {};
	for (const field of schema.fields) {
		switch (field.type) {
			case "numeric": {
				defaults[field.id] = [field.min ?? 0, field.max ?? 100];
				break;
			}
			case "date": {
				defaults[field.id] = {
					startDate: field.minDate ?? "",
					endDate: field.maxDate ?? "",
				};
				break;
			}
			case "text": {
				defaults[field.id] = []; // Empty array for multi-select
				break;
			}
			// No default for other types
		}
	}
	return defaults;
}

// Helper function to generate mock data for testing
function generateMockData(count: number, fields: any[]): any[] {
	const data: any[] = [];

	for (let i = 0; i < count; i++) {
		const record: any = {
			id: i + 1,
		};

		for (const field of fields) {
			switch (field.type) {
				case "text": {
					record[field.name] =
						field.options && field.options.length > 0
							? field.options[Math.floor(Math.random() * field.options.length)]
							: `Sample ${field.name} ${i + 1}`;
					break;
				}
				case "numeric": {
					const min = field.min ?? 0;
					const max = field.max ?? 100;
					record[field.name] =
						Math.floor(Math.random() * (max - min + 1)) + min;
					break;
				}
				case "date": {
					const start = new Date(field.minDate ?? "2020-01-01");
					const end = new Date(field.maxDate ?? "2024-12-31");
					const randomTime =
						start.getTime() + Math.random() * (end.getTime() - start.getTime());
					record[field.name] = new Date(randomTime).toISOString().split("T")[0];
					break;
				}
				default: {
					record[field.name] = `Value ${i + 1}`;
				}
			}
		}

		data.push(record);
	}

	return data;
}

export interface UseQueryStateReturn {
	// State
	selectedQuery: QueryConfig | null;
	filterValues: Record<string, any>;
	spatialSelection: string | null;
	loading: boolean;
	error: string | null;
	/** @deprecated mock data only */
	results: QueryResults | null;
	/** @deprecated mock data only */
	queryError: QueryErrorState | null;
	showResults: boolean;

	// Actions
	handleQueryChange: (query: QueryConfig | null) => void;
	handleFilterChange: (fieldId: string, value: any) => void;
	handleSpatialSelectionChange: (toolId: string | null) => void;
	handleClear: () => void;
	handleSubmit: () => Promise<void>;
	handleRetry: () => void;
	handleNewQuery: () => void;

	// Computed properties
	canSubmit: boolean;
	hasFilters: boolean;
	hasSpatialSelection: boolean;
	canClear: boolean;
}

export function useQueryState({
	queries,
	lastUsedQuery,
	simulateError = false,
	simulateResults,
	onQuerySelect,
	onFilterChange,
	onSpatialSelectionChange,
	onClear,
	onSubmit,
}: UseQueryStateProps): UseQueryStateReturn {
	// Track if any query has been manually selected (for clear button state)
	const [hasQueryBeenSelected, setHasQueryBeenSelected] = React.useState(false);

	// Initialize selected query based on available options
	const [selectedQuery, setSelectedQuery] = React.useState<QueryConfig | null>(
		null,
	);

	React.useEffect(() => {
		const getSelectedQuery = () => {
			if (queries.length === 1) {
				// Auto-select if only one query available
				return queries[0] ?? null;
			}
			if (lastUsedQuery) {
				// Remember last used query from session
				const found = queries.find((q) => q.id === lastUsedQuery);
				return found ?? null;
			}
			return null;
		};

		setSelectedQuery(getSelectedQuery());
		onQuerySelect?.(getSelectedQuery());
	}, [queries, lastUsedQuery]);

	const [filterValues, setFilterValues] = React.useState<Record<string, any>>(
		{},
	);
	const [spatialSelection, setSpatialSelection] = React.useState<string | null>(
		null,
	);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [results, setResults] = React.useState<QueryResults | null>(null);
	const [queryError, setQueryError] = React.useState<QueryErrorState | null>(
		null,
	);
	const [showResults, setShowResults] = React.useState(false);

	// Simulate loading configuration when query changes
	React.useEffect(() => {
		if (selectedQuery) {
			setError(null);

			// Reset filter values to defaults when switching queries
			if (selectedQuery.schema) {
				const defaultValues = getDefaultFilterValues(selectedQuery.schema);
				setFilterValues(defaultValues);
			} else {
				setFilterValues({});
			}
			setSpatialSelection(null);

			if (simulateError) {
				setTimeout(() => {
					setError("Unable to load query configuration");
				}, 1000);
			}
		}
	}, [selectedQuery, simulateError]);

	const handleQueryChange = React.useCallback(
		(query: QueryConfig | null) => {
			setSelectedQuery(query);
			setHasQueryBeenSelected(true); // Mark that user has selected a query
			onQuerySelect?.(query);
		},
		[onQuerySelect],
	);

	const handleFilterChange = React.useCallback(
		(fieldId: string, value: any) => {
			setFilterValues((prev) => {
				const newFilters = {
					...prev,
					[fieldId]: value,
				};
				onFilterChange?.(fieldId, value, newFilters);
				return newFilters;
			});
		},
		[onFilterChange],
	);

	const handleSpatialSelectionChange = React.useCallback(
		(toolId: string | null) => {
			setSpatialSelection(toolId);
			onSpatialSelectionChange?.(toolId);
		},
		[onSpatialSelectionChange],
	);

	const handleClear = React.useCallback(() => {
		// Keep the current selectedQuery unchanged
		// Reset filters to their default values based on field definitions
		if (selectedQuery?.schema) {
			const defaultValues = getDefaultFilterValues(selectedQuery.schema);
			setFilterValues(defaultValues);
		} else {
			setFilterValues({});
		}
		setSpatialSelection(null);
		setError(null);
		onClear?.();
	}, [selectedQuery, onClear]);

	const handleSubmit = React.useCallback(async () => {
		if (!selectedQuery) return;

		setLoading(true);
		setError(null);
		setQueryError(null);
		setResults(null);

		// Simulate query execution
		if (simulateResults) {
			setTimeout(() => {
				// Generate mock results based on simulation type
				switch (simulateResults) {
					case "success": {
						const mockResults: QueryResults = {
							id: `query-${Date.now()}`,
							queryConfig: selectedQuery,
							resultCount: 156,
							data: generateMockData(156, selectedQuery.schema?.fields ?? []),
							report: selectedQuery.schema?.fields?.length
								? {
										enabled: true,
										content:
											"This query successfully returned 156 matching records from the dataset. The results show a good distribution across the selected criteria.",
										summary:
											"Analysis of query results shows strong correlation with search parameters.",
									}
								: undefined,
							metadata: {
								executionTime: Math.random() * 2000 + 500,
								timestamp: new Date().toISOString(),
								spatialExtent: spatialSelection
									? {
											minX: -122.5,
											minY: 37.7,
											maxX: -122.3,
											maxY: 37.9,
										}
									: undefined,
							},
							status: "success",
						};
						setResults(mockResults);
						setShowResults(true);

						break;
					}
					case "no_results": {
						const errorState: QueryErrorState = {
							type: "no_results",
							message: "No results found for your current search criteria.",
							appliedFilters: filterValues,
							spatialSelection: spatialSelection ?? undefined,
							suggestions: [
								"Try expanding your search area",
								"Adjust filter criteria to be less restrictive",
								"Check if the selected layer contains data for this region",
							],
						};
						setQueryError(errorState);

						break;
					}
					case "too_many_results": {
						const errorState: QueryErrorState = {
							type: "too_many_results",
							message:
								"Your query returned too many results (10,000+) and cannot be displayed.",
							appliedFilters: filterValues,
							spatialSelection: spatialSelection ?? undefined,
							suggestions: [
								"Add more specific filter criteria",
								"Reduce the search area",
								"Narrow the date range if applicable",
								"Use more restrictive categorical filters",
							],
						};
						setQueryError(errorState);

						break;
					}
					default: {
						const errorState: QueryErrorState = {
							type: "query_failed",
							message:
								"An error occurred while executing the query. Please try again.",
							appliedFilters: filterValues,
							spatialSelection: spatialSelection ?? undefined,
							suggestions: [
								"Check your network connection",
								"Verify the selected layer is available",
								"Try again in a few moments",
							],
						};
						setQueryError(errorState);
					}
				}
				setLoading(false);
			}, 1500);
		} else {
			await onSubmit?.({
				selectedQuery,
				filterValues,
				spatialSelection,
				loading: true,
				error,
			});

			const placeholderResults: QueryResults = {
				id: `query-${Date.now()}`,
				queryConfig: selectedQuery,
				resultCount: 0,
				data: [],
				report: undefined,
				metadata: {
					executionTime: 0,
					timestamp: new Date().toISOString(),
				},
				status: "no_results",
			};
			setResults(placeholderResults);
			setShowResults(true);
			setLoading(false);
		}
	}, [
		selectedQuery,
		filterValues,
		spatialSelection,
		error,
		onSubmit,
		simulateResults,
	]);

	const handleRetry = React.useCallback(() => {
		setError(null);
		setQueryError(null);
		if (selectedQuery) {
			setLoading(true);
			onSubmit?.({
				selectedQuery,
				filterValues,
				spatialSelection,
				loading: true,
				error,
			})
				.then(() => {
					setLoading(false);
				})
				.catch(() => {
					setLoading(false);
				});
		}
	}, [onSubmit, selectedQuery, filterValues, spatialSelection, error]);

	const handleNewQuery = React.useCallback(() => {
		setResults(null);
		setQueryError(null);
		setShowResults(false);
		setError(null);
	}, []);

	return {
		// State
		selectedQuery,
		filterValues,
		spatialSelection,
		loading,
		error,
		results,
		queryError,
		showResults,

		// Actions
		handleQueryChange,
		handleFilterChange,
		handleSpatialSelectionChange,
		handleClear,
		handleSubmit,
		handleRetry,
		handleNewQuery,

		// Computed properties
		canSubmit:
			!!selectedQuery &&
			!loading &&
			(!selectedQuery?.schema?.spatialSelection?.enabled || !!spatialSelection),
		hasFilters:
			selectedQuery?.schema?.fields && selectedQuery.schema.fields.length > 0,
		hasSpatialSelection:
			selectedQuery?.schema?.spatialSelection?.enabled ?? false,
		canClear: Boolean(hasQueryBeenSelected), // Clear button only enabled after query selection
	} as UseQueryStateReturn;
}
